import React, { useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useCommandPalette } from '@/hooks/useCommandPalette';

export default function CommandPalette() {
  const { isOpen, setIsOpen, search, setSearch, commands, executeCommand } = useCommandPalette();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus input when opened
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="p-0 gap-0 max-w-2xl">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-base">Quick Navigation</DialogTitle>
        </DialogHeader>

        <div className="px-4 pb-2">
          <Input
            ref={inputRef}
            placeholder="Search pages and actions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border-0 focus-visible:ring-0 shadow-none"
          />
        </div>

        <Command className="border-0">
          <CommandList className="max-h-[400px]">
            <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </CommandEmpty>
            <CommandGroup>
              {commands.map((cmd) => (
                <CommandItem
                  key={cmd.id}
                  onSelect={() => executeCommand(cmd)}
                  className="flex items-center justify-between px-4 py-3 cursor-pointer"
                >
                  <div className="flex flex-col gap-1">
                    <div className="font-medium">{cmd.title}</div>
                    {cmd.description && (
                      <div className="text-xs text-muted-foreground">{cmd.description}</div>
                    )}
                  </div>
                  {cmd.shortcut && (
                    <Badge variant="secondary" className="text-xs font-mono">
                      {cmd.shortcut}
                    </Badge>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>

        <div className="px-4 py-2 border-t bg-muted/50 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>
              <kbd className="px-1.5 py-0.5 text-xs bg-background rounded border">↑↓</kbd> Navigate
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 text-xs bg-background rounded border">Enter</kbd> Select
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 text-xs bg-background rounded border">Esc</kbd> Close
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
